import { useEffect, useRef, useState } from 'react';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { Crosshair, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';

// Workaround para íconos de Leaflet con bundlers (Vite)
// eslint-disable-next-line @typescript-eslint/no-explicit-any
delete (L.Icon.Default.prototype as any)._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png',
  iconUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png',
  shadowUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png',
});

interface Props {
  latitude: number | null;
  longitude: number | null;
  onChange: (lat: number, lng: number) => void;
  height?: number;
}

export function LocationMapPicker({ latitude, longitude, onChange, height = 240 }: Props) {
  const mapContainerRef = useRef<HTMLDivElement | null>(null);
  const mapRef = useRef<L.Map | null>(null);
  const markerRef = useRef<L.Marker | null>(null);
  const [locating, setLocating] = useState(false);
  const [geoError, setGeoError] = useState<string | null>(null);

  // Centrar el mapa en la ubicación actual del usuario y plantar el marcador.
  const useCurrentLocation = () => {
    if (!('geolocation' in navigator)) {
      setGeoError('Tu navegador no soporta geolocalización');
      return;
    }
    setGeoError(null);
    setLocating(true);
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setLocating(false);
        const lat = Number(pos.coords.latitude.toFixed(7));
        const lng = Number(pos.coords.longitude.toFixed(7));
        onChange(lat, lng);
        if (mapRef.current) {
          mapRef.current.setView([lat, lng], 15);
        }
      },
      (err) => {
        setLocating(false);
        const msg =
          err.code === err.PERMISSION_DENIED
            ? 'Permiso denegado para acceder a tu ubicación'
            : err.code === err.POSITION_UNAVAILABLE
              ? 'Ubicación no disponible'
              : err.code === err.TIMEOUT
                ? 'Timeout al obtener ubicación'
                : 'No se pudo obtener la ubicación';
        setGeoError(msg);
      },
      { enableHighAccuracy: true, timeout: 10000, maximumAge: 0 }
    );
  };

  // Al montar, si no hay coords todavía, intentamos centrar el mapa en la
  // ubicación actual del usuario (sin marcador — el usuario decide dónde fijar).
  useEffect(() => {
    if (latitude != null || longitude != null) return;
    if (!('geolocation' in navigator)) return;
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        if (mapRef.current && markerRef.current == null) {
          mapRef.current.setView([pos.coords.latitude, pos.coords.longitude], 13);
        }
      },
      () => {
        // Silencioso — si falla quedamos con el default (La Paz)
      },
      { enableHighAccuracy: false, timeout: 5000, maximumAge: 60000 }
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Inicializar mapa una sola vez
  useEffect(() => {
    if (!mapContainerRef.current || mapRef.current) return;

    const initialLat = latitude ?? -16.5; // La Paz por defecto
    const initialLng = longitude ?? -68.15;

    const map = L.map(mapContainerRef.current, {
      center: [initialLat, initialLng],
      zoom: latitude != null && longitude != null ? 13 : 5,
      scrollWheelZoom: false,
    });

    L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      attribution: '© OpenStreetMap',
      maxZoom: 19,
    }).addTo(map);

    if (latitude != null && longitude != null) {
      markerRef.current = L.marker([latitude, longitude], { draggable: true }).addTo(map);
      markerRef.current.on('dragend', () => {
        const pos = markerRef.current!.getLatLng();
        onChange(Number(pos.lat.toFixed(7)), Number(pos.lng.toFixed(7)));
      });
    }

    map.on('click', (e: L.LeafletMouseEvent) => {
      const { lat, lng } = e.latlng;
      const fixedLat = Number(lat.toFixed(7));
      const fixedLng = Number(lng.toFixed(7));
      if (markerRef.current) {
        markerRef.current.setLatLng([fixedLat, fixedLng]);
      } else {
        markerRef.current = L.marker([fixedLat, fixedLng], { draggable: true }).addTo(map);
        markerRef.current.on('dragend', () => {
          const pos = markerRef.current!.getLatLng();
          onChange(Number(pos.lat.toFixed(7)), Number(pos.lng.toFixed(7)));
        });
      }
      onChange(fixedLat, fixedLng);
    });

    mapRef.current = map;

    // Forzar resize después del render (para dialogs)
    setTimeout(() => map.invalidateSize(), 100);

    return () => {
      map.remove();
      mapRef.current = null;
      markerRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Sincronizar marker cuando los inputs cambian externamente
  useEffect(() => {
    if (!mapRef.current) return;
    if (latitude != null && longitude != null) {
      if (markerRef.current) {
        markerRef.current.setLatLng([latitude, longitude]);
      } else {
        markerRef.current = L.marker([latitude, longitude], { draggable: true }).addTo(
          mapRef.current
        );
        markerRef.current.on('dragend', () => {
          const pos = markerRef.current!.getLatLng();
          onChange(Number(pos.lat.toFixed(7)), Number(pos.lng.toFixed(7)));
        });
      }
      mapRef.current.setView([latitude, longitude], Math.max(mapRef.current.getZoom(), 12));
    } else if (markerRef.current) {
      markerRef.current.remove();
      markerRef.current = null;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [latitude, longitude]);

  return (
    <div className="space-y-2">
      <div className="relative">
        <div
          ref={mapContainerRef}
          className="w-full rounded-md border border-border overflow-hidden"
          style={{ height: `${height}px` }}
        />
        <Button
          type="button"
          size="sm"
          variant="secondary"
          onClick={useCurrentLocation}
          disabled={locating}
          className="absolute top-2 right-2 z-[1000] shadow-md"
          title="Usar mi ubicación actual"
        >
          {locating ? (
            <Loader2 className="w-3.5 h-3.5 animate-spin" />
          ) : (
            <Crosshair className="w-3.5 h-3.5" />
          )}
          <span className="ml-1 text-xs">Mi ubicación</span>
        </Button>
      </div>
      {geoError && <p className="text-xs text-destructive">{geoError}</p>}
    </div>
  );
}
